import { Component, type ErrorInfo, type ReactNode } from "react";
import { Sentry, sentryEnabled } from "@/lib/sentry";
import { isChunkLoadError, reportChunkError, maybeReloadForStaleChunks } from "@/lib/chunk-errors";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * App-wide error boundary. Catches render/runtime errors in the React tree
 * and shows a graceful fallback instead of a blank screen.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep signal in the console for debugging.
    console.error("[ErrorBoundary]", error, info.componentStack);

    // Stale deploy: report as a chunk-load failure and self-heal once.
    if (isChunkLoadError(error)) {
      reportChunkError(error, "app-root", { phase: "render" });
      maybeReloadForStaleChunks();
      return;
    }

    if (sentryEnabled) {
      Sentry.withScope((scope) => {
        scope.setTag("boundary", "app-root");
        scope.setContext("react", { componentStack: info.componentStack });
        Sentry.captureException(error);
      });
    }
  }


  handleReload = () => {
    try {
      // Best-effort clean-slate: drop any stale cached state.
      sessionStorage.clear();
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-4">
          <h1 className="text-2xl font-semibold">Something went wrong</h1>
          <p className="text-sm text-muted-foreground">
            An unexpected error occurred while loading this page. You can reload
            to try again — your account and data are safe.
          </p>
          {this.state.error?.message ? (
            <pre className="text-xs text-left bg-muted p-3 rounded overflow-auto max-h-40">
              {this.state.error.message}
            </pre>
          ) : null}
          <button
            onClick={this.handleReload}
            className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 transition"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
